// src/pages/NovoCaso/index.js
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useClient } from '../../contexts/ClientContext';
import axios from 'axios';
import AnexarUnificadoDialog from '../../components/dialogs/AnexarUnificadoDialog';
import './NovoCaso.css';

const NovoCaso = () => {
  const [nomeCaso, setNomeCaso] = useState('');
  const [codigoCliente, setCodigoCliente] = useState('');
  const [descricao, setDescricao] = useState('');
  const [anexos, setAnexos] = useState([]); // [{ nome, conteudo }]
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [error, setError] = useState('');
  const [dialogAnexar, setDialogAnexar] = useState(false);
  const navigate = useNavigate();
  const { user, refreshAccessToken } = useAuth();
  const { clienteAtual } = useClient();
  const keepAliveIntervalRef = useRef(null);

  useEffect(() => {
    if (clienteAtual.codCliente && !codigoCliente) {
      setCodigoCliente(clienteAtual.codCliente);
    }
  }, [clienteAtual?.codCliente, codigoCliente]);

  const startKeepAlive = () => {
    keepAliveIntervalRef.current = setInterval(() => {
      refreshAccessToken();
    }, 20 * 60 * 1000);
  };

  const stopKeepAlive = () => {
    if (keepAliveIntervalRef.current) {
      clearInterval(keepAliveIntervalRef.current);
      keepAliveIntervalRef.current = null;
    }
  };

  useEffect(() => {
    return () => { stopKeepAlive(); };
  }, []);

  const handleAnexar = (novosAnexos) => {
    setAnexos(prev => {
      const nomesExistentes = new Set(prev.map(a => a.nome));
      const novos = novosAnexos.filter(a => !nomesExistentes.has(a.nome));
      return [...prev, ...novos];
    });
    setDialogAnexar(false);
  };

  const handleRemoverAnexo = (index) => {
    setAnexos(prev => prev.filter((_, i) => i !== index));
  };

  const buildDescricaoCompleta = () => {
    const base = descricao.trim();
    if (anexos.length === 0) return base;
    const separador = base ? '\n\n---\n\n' : '';
    const textosAnexos = anexos
      .map(a => `DOCUMENTO ANEXO - ${a.nome}:\n\n${a.conteudo}`)
      .join('\n\n---\n\n');
    return base + separador + textosAnexos;
  };

  const chamadaComRetry = async (chamadaFn, maxTentativas = 2, nomeOperacao = 'operação') => {
    let ultimoErro = null;
    for (let tentativa = 1; tentativa <= maxTentativas; tentativa++) {
      try {
        setStatusMessage(`${nomeOperacao}... ${tentativa > 1 ? `(tentativa ${tentativa}/${maxTentativas})` : '(pode demorar alguns minutos)'}`);
        const resultado = await chamadaFn();
        return { sucesso: true, dados: resultado };
      } catch (err) {
        ultimoErro = err;
        console.error(`Erro na tentativa ${tentativa} de ${nomeOperacao}:`, err);
        if (err.response?.status === 500 && tentativa < maxTentativas) {
          setStatusMessage(`${nomeOperacao} falhou (erro 500), tentando novamente... (tentativa ${tentativa + 1}/${maxTentativas})`);
          await new Promise(resolve => setTimeout(resolve, 2000));
          continue;
        }
        if (err.response?.status !== 500) break;
      }
    }
    return { sucesso: false, erro: ultimoErro };
  };

  const handleAnalisarCriar = async () => {
    if (!nomeCaso.trim()) {
      setError('Nome do caso é obrigatório');
      return;
    }
    if (!descricao.trim() && anexos.length === 0) {
      setError('É necessário fornecer uma descrição ou anexar documentos');
      return;
    }

    setLoading(true);
    setError('');
    setStatusMessage('Iniciando análise...');
    startKeepAlive();

    const descricaoCompleta = buildDescricaoCompleta();
    console.log(`📝 Descrição completa: ${descricao.trim().length} chars base + ${anexos.length} anexo(s)`);

    let avisosFalhas = [];

    try {
      await refreshAccessToken();

      const dadosCaso = {
        name: nomeCaso.trim(),
        cod_cli: codigoCliente ? parseInt(codigoCliente) : null,
        description: descricaoCompleta,
        user_email: user.email,
        dados_cliente: null,
        analise_caso: null,
        documentos_sugeridos: null,
        arquivosProcessados: null
      };

      if (codigoCliente) {
        setStatusMessage('Recolhendo Informação do Cliente...');
        try {
          const clienteResponse = await axios.get(`/api/clients/${codigoCliente}`);
          if (clienteResponse.data.success) {
            dadosCaso.dados_cliente = JSON.stringify(clienteResponse.data.data);
          }
        } catch (err) {
          console.error('Erro ao buscar dados do cliente:', err);
        }
      }

      const statusAnalise = anexos.length > 0
        ? `Analisando Caso (${anexos.length} documento(s) anexado(s))...`
        : 'Analisando Caso...';
      setStatusMessage(statusAnalise);

      const resultadoAnalise = await chamadaComRetry(
        () => axios.post('/api/casos/analisar', { query: descricaoCompleta }, { timeout: 600000 }),
        2,
        'Analisando Caso'
      );

      if (resultadoAnalise.sucesso) {
        const analiseResponse = resultadoAnalise.dados;
        if (analiseResponse.data.success && analiseResponse.data.data) {
          let analiseLimpa = analiseResponse.data.data;
          while (analiseLimpa && typeof analiseLimpa === 'object' && 'results' in analiseLimpa) {
            analiseLimpa = analiseLimpa.results;
          }
          dadosCaso.analise_caso = JSON.stringify({ results: analiseLimpa });
        }
      } else {
        console.error('Falha ao analisar caso após todas as tentativas:', resultadoAnalise.erro);
        avisosFalhas.push('análise do caso');
      }

      await refreshAccessToken();

      const resultadoDocumentos = await chamadaComRetry(
        () => axios.post('/api/casos/sugerir-documentos', {
          query: descricaoCompleta,
          analise: dadosCaso.analise_caso ? JSON.parse(dadosCaso.analise_caso) : null
        }, { timeout: 300000 }),
        2,
        'Sugerindo Documentos'
      );

      if (resultadoDocumentos.sucesso) {
        const documentosResponse = resultadoDocumentos.dados;
        if (documentosResponse.data?.data) {
          dadosCaso.documentos_sugeridos = JSON.stringify(documentosResponse.data.data);
        }
      } else {
        console.error('Falha ao sugerir documentos:', resultadoDocumentos.erro);
        avisosFalhas.push('documentos sugeridos');
      }

      setStatusMessage('Salvando Caso...');
      const salvarResponse = await axios.post('/api/casos/criar', dadosCaso);

      if (salvarResponse.data.success) {
        let mensagemSucesso = '✅ CASO CRIADO COM SUCESSO!';
        if (anexos.length > 0) {
          mensagemSucesso += `\n📎 ${anexos.length} documento(s) incluído(s) na descrição`;
        }
        if (avisosFalhas.length > 0) {
          mensagemSucesso += `\n\n⚠️ Atenção: ${avisosFalhas.join(' e ')} não foram salvos devido a instabilidade da API.`;
        }
        setStatusMessage(mensagemSucesso);
        setTimeout(() => {
          navigate(`/gestao-casos/${salvarResponse.data.data.id}`);
        }, avisosFalhas.length > 0 ? 4000 : 1500);
      } else {
        setError('Erro ao criar caso');
      }

    } catch (err) {
      console.error('Erro ao criar caso:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        setError('Sessão expirada. Por favor, faça login novamente.');
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setError('Erro ao processar caso: ' + (err.response?.data?.message || err.message));
      }
    } finally {
      setLoading(false);
      stopKeepAlive();
    }
  };

  const handleVoltar = () => { navigate('/gestao-casos'); };

  const isSubmitDisabled = loading
    || !nomeCaso.trim()
    || (!descricao.trim() && anexos.length === 0);

  return (
    <div className="novo-caso-container">
      <div className="novo-caso-header">
        <button className="btn-voltar" onClick={handleVoltar} disabled={loading}>← Voltar</button>
        <h2>➕ Novo Caso Jurídico</h2>
      </div>

      <div className="novo-caso-content">
        <div className="novo-caso-form">

          <div className="form-group">
            <label htmlFor="nomeCaso">Nome do Caso</label>
            <input
              type="text"
              id="nomeCaso"
              value={nomeCaso}
              onChange={(e) => setNomeCaso(e.target.value)}
              placeholder='"Assédio Arrendamento - Silva vs. Fernandes"'
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="codigoCliente">
              Código do Cliente - opcional
              <span className="label-hint">(Para preencher dados automaticamente)</span>
            </label>
            <input
              type="text"
              id="codigoCliente"
              value={codigoCliente}
              onChange={(e) => setCodigoCliente(e.target.value)}
              placeholder="00000"
              maxLength="5"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="descricao">
              Descreva detalhadamente o caso jurídico...
              <span className="label-hint">(Opcional se anexar documentos)</span>
            </label>
            <textarea
              id="descricao"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              placeholder="Descreva todos os detalhes relevantes do caso ou utilize a opção de anexo abaixo..."
              rows={8}
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label>
              📎 Documentos (Opcional)
              <span className="label-hint">
                PDF, DOCX, TXT, imagens (OCR) ou áudio — texto extraído e incluído no contexto de análise
              </span>
            </label>

            <div className="file-upload-area">
              <button
                type="button"
                className={`file-upload-label ${loading ? 'disabled' : ''}`}
                onClick={() => setDialogAnexar(true)}
                disabled={loading}
              >
                <span className="upload-icon">📂</span>
                <span className="upload-text">Anexar Documentos / Áudio</span>
              </button>
            </div>

            {anexos.length > 0 && (
              <div className="selected-files-list">
                <div className="selected-files-header">
                  <span>Documentos Anexados ({anexos.length})</span>
                </div>
                {anexos.map((anexo, index) => (
                  <div key={index} className="selected-file-item">
                    <div className="file-info">
                      <span className="file-icon">📄</span>
                      <div className="file-details">
                        <span className="file-name">{anexo.nome}</span>
                        <span className="file-size">{Math.round(anexo.conteudo.length / 1000)}k chars</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn-remove-file"
                      onClick={() => handleRemoverAnexo(index)}
                      disabled={loading}
                      title="Remover"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {statusMessage && (
            <div className={`status-message ${statusMessage.includes('✅') ? 'success' : ''} ${statusMessage.includes('⚠️') ? 'warning' : ''}`}>
              {!statusMessage.includes('✅') && !statusMessage.includes('⚠️') && <div className="spinner"></div>}
              <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{statusMessage}</pre>
            </div>
          )}

          {error && (
            <div className="error-message">
              <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{error}</pre>
            </div>
          )}

          <button
            className="btn-analisar-criar"
            onClick={handleAnalisarCriar}
            disabled={isSubmitDisabled}
          >
            {loading ? 'Processando...' : 'ANALISAR E CRIAR CASO'}
          </button>
        </div>
      </div>

      {dialogAnexar && (
        <AnexarUnificadoDialog
          onClose={() => setDialogAnexar(false)}
          onAnexar={handleAnexar}
          codCli={codigoCliente ? parseInt(codigoCliente) : null}
          titulo="Anexar Documentos ao Caso"
        />
      )}
    </div>
  );
};

export default NovoCaso;
